import { useTranslation } from "react-i18next";
import { Sparkle, Drop, Lightning, Heart, ArrowsClockwise, WhatsappLogo } from "@phosphor-icons/react";
import { buildWhatsAppUrl, trackWhatsAppClick } from "../lib/whatsapp";

const OTHER_ICONS = [Lightning, ArrowsClockwise, Drop, Heart, Sparkle];

const PriceTier = ({ label, price, variant }) => (
  <div
    className={`flex flex-col items-start justify-center px-4 py-3 rounded-md border ${
      variant === "premium"
        ? "bg-[#3E5239] border-[#3E5239] text-[#F6F5F2]"
        : "bg-[#F6F5F2] border-[#E4E2DA] text-[#3E5239]"
    }`}
  >
    <div
      className={`flex items-center gap-1.5 text-[10px] uppercase tracking-[0.2em] font-semibold ${
        variant === "premium" ? "text-[#A7CA99]" : "text-[#6B7A66]"
      }`}
    >
      {variant === "premium" && <Sparkle size={10} weight="fill" />}
      {label}
    </div>
    <div
      className={`font-serif text-xl lg:text-2xl mt-1 tabular-nums font-normal ${
        variant === "premium" ? "text-[#F6F5F2]" : "text-[#3E5239]"
      }`}
    >
      {price}
    </div>
  </div>
);

/**
 * Full reference price tables — appears ONLY on the dedicated prices page
 * (/preise, /precos, /prices). The homepage shows the promise variant only.
 */
export const PriceTablesFull = () => {
  const { t } = useTranslation();
  const zones = t("prices.botox_zones", { returnObjects: true });
  const others = t("prices.others_items", { returnObjects: true });

  return (
    <section data-testid="prices-full-tables" className="py-16 lg:py-24 bg-[#F6F5F2] relative overflow-hidden">
      <div className="relative max-w-7xl mx-auto px-6 md:px-12">
        {/* BOTOX */}
        <div className="mb-12 bg-white rounded-lg overflow-hidden shadow-[0_10px_40px_-15px_rgba(62,82,57,0.15)] border border-[#E4E2DA]">
          <div className="px-6 lg:px-10 pt-8 pb-6 bg-gradient-to-b from-[#A7CA99]/8 to-transparent border-b border-[#E4E2DA]">
            <h2 className="font-serif text-3xl lg:text-4xl font-light text-[#3E5239]">{t("prices.botox_title")}</h2>
            <p className="text-sm text-[#6B7A66] mt-2 font-light max-w-md">{t("prices.botox_intro")}</p>

            <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="flex items-start gap-3 p-4 bg-[#3E5239] rounded-md">
                <Sparkle size={18} weight="fill" className="text-[#A7CA99] shrink-0 mt-0.5" />
                <div>
                  <div className="text-[11px] uppercase tracking-[0.22em] text-[#A7CA99] font-semibold">
                    {t("prices.col_premium")}
                  </div>
                  <div className="text-xs text-[#F6F5F2]/85 mt-1 font-light leading-snug">
                    {t("prices.botox_premium_subtitle")}
                  </div>
                </div>
              </div>
              <div className="flex items-start gap-3 p-4 bg-[#F6F5F2] border border-[#E4E2DA] rounded-md">
                <div className="w-[18px] h-[18px] rounded-full border border-[#6B7A66] shrink-0 mt-0.5" />
                <div>
                  <div className="text-[11px] uppercase tracking-[0.22em] text-[#3E5239] font-semibold">
                    {t("prices.col_standard")}
                  </div>
                  <div className="text-xs text-[#6B7A66] mt-1 font-light leading-snug">
                    {t("prices.botox_standard_subtitle")}
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="px-6 lg:px-10 py-4 divide-y divide-[#E4E2DA]">
            {zones.map((z, i) => (
              <div key={z.label} data-testid={`price-zone-${i}`} className="py-5 hover:bg-[#A7CA99]/5 transition-colors -mx-3 px-3 rounded-md">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 lg:gap-6 lg:items-center">
                  <div className="lg:col-span-5">
                    <div className="text-[#3E5239] font-medium text-base lg:text-lg">{z.label}</div>
                    {z.sub && <div className="text-[12px] text-[#6B7A66] font-light mt-0.5">{z.sub}</div>}
                  </div>
                  <div className="lg:col-span-7 grid grid-cols-2 gap-3">
                    <PriceTier label={t("prices.col_premium")} price={z.premium} variant="premium" />
                    <PriceTier label={t("prices.col_standard")} price={z.standard} variant="standard" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* OTHERS */}
        <div className="bg-white rounded-lg overflow-hidden shadow-[0_10px_40px_-15px_rgba(62,82,57,0.15)] border border-[#E4E2DA]">
          <div className="px-6 lg:px-10 pt-8 pb-6 bg-gradient-to-b from-[#A7CA99]/8 to-transparent border-b border-[#E4E2DA]">
            <h2 className="font-serif text-3xl lg:text-4xl font-light text-[#3E5239]">{t("prices.others_title")}</h2>
            <p className="text-sm text-[#6B7A66] mt-2 font-light">{t("prices.others_intro")}</p>
          </div>
          <div className="px-6 lg:px-10 py-2 divide-y divide-[#E4E2DA]">
            {others.map((o, i) => {
              const Icon = OTHER_ICONS[i] || Sparkle;
              return (
                <div
                  key={o.label}
                  data-testid={`price-other-${i}`}
                  className="flex items-center justify-between gap-4 py-5 hover:bg-[#A7CA99]/5 transition-colors -mx-3 px-3 rounded-md"
                >
                  <div className="flex items-start gap-3 min-w-0 flex-1">
                    <span className="w-10 h-10 rounded-full bg-[#A7CA99]/15 flex items-center justify-center shrink-0">
                      <Icon size={16} weight="thin" className="text-[#3E5239]" />
                    </span>
                    <div className="min-w-0">
                      <div className="text-[#3E5239] font-medium text-base lg:text-lg">{o.label}</div>
                      {o.sub && <div className="text-[12px] text-[#6B7A66] font-light mt-0.5 leading-snug">{o.sub}</div>}
                    </div>
                  </div>
                  <div className="text-right shrink-0">
                    <div className="font-serif text-xl lg:text-2xl text-[#3E5239] font-normal tabular-nums">{o.price}</div>
                    {o.perSession && <div className="text-[11px] text-[#6B7A66] font-light mt-0.5">{o.perSession}</div>}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <div className="mt-8 grid sm:grid-cols-3 gap-4">
          {t("prices.notes", { returnObjects: true }).map((n) => (
            <div key={n} data-testid="price-note" className="flex items-start gap-2 text-xs text-[#6B7A66] font-light">
              <span className="text-[#A7CA99] mt-0.5">✓</span>
              <span>{n}</span>
            </div>
          ))}
        </div>

        <div className="mt-12 flex justify-center">
          <a
            href={buildWhatsAppUrl()}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => trackWhatsAppClick("prices_page_cta")}
            data-testid="prices-book-link"
            className="inline-flex items-center gap-2 px-7 py-3.5 bg-[#25D366] hover:bg-[#1DA851] text-white text-xs uppercase tracking-[0.22em] transition-colors rounded-sm"
          >
            <WhatsappLogo size={14} weight="fill" />
            {t("nav.book")}
          </a>
        </div>
      </div>
    </section>
  );
};
